import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetRequest, sendPatchRequest } from '../../../api/requestUtil';
import { ClearingDetail, ClearingDumpResult } from '../types/terrain.types';

const CLEARING_DETAIL_TTL = 15 * 60 * 1000;

export const fetchClearingDetail = createAsyncThunk<ClearingDetail, string>(
  'clearing/fetchDetail',
  async (slug: string, { getState, rejectWithValue }) => {
    const state = (getState() as AppState).clearing;
    if (
      state.cachedSlug === slug &&
      state.lastFetched &&
      Date.now() - state.lastFetched < CLEARING_DETAIL_TTL
    ) {
      return state.detail!;
    }
    try {
      return await sendGetRequest(`/api/terrain/clearings/${slug}/`);
    } catch (error: any) {
      return rejectWithValue(error?.detail || 'Failed to load clearing');
    }
  }
);

export const fetchClearingDump = createAsyncThunk<
  ClearingDumpResult,
  { slug: string; type: 'articles' | 'blogs'; sort: 'saves' | 'recent'; page: number }
>(
  'clearing/fetchDump',
  async ({ slug, type, sort, page }, { rejectWithValue }) => {
    try {
      return await sendGetRequest(
        `/api/terrain/clearings/${slug}/all/?type=${type}&sort=${sort}&page=${page}`
      );
    } catch (error: any) {
      return rejectWithValue(error?.detail || 'Failed to load clearing content');
    }
  }
);

export const fetchClearingDiscussions = createAsyncThunk<
  BlogDiscussionsListSuccessType,
  number
>(
  'clearing/fetchDiscussions',
  async (clearingId: number, { rejectWithValue }) => {
    try {
      return await sendGetRequest(`/api/clearings/${clearingId}/discussions/`);
    } catch (error: any) {
      return rejectWithValue(error?.detail || 'Failed to load discussions');
    }
  }
);

export const updateClearingContent = createAsyncThunk<
  ClearingDetail,
  { slug: string; description?: string; pinned_post_ids?: number[] },
  { state: AppState; rejectValue: { detail: string } }
>(
  'clearing/updateContent',
  async ({ slug, description, pinned_post_ids }, { getState, rejectWithValue }) => {
    const token = getState().user.authToken;
    try {
      return await sendPatchRequest(
        `/api/terrain/clearings/${slug}/`,
        { description, pinned_post_ids },
        token
      );
    } catch (error: any) {
      return rejectWithValue({ detail: error?.detail || 'Failed to update clearing' });
    }
  }
);
